import { useEffect, useState, useRef } from "react";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Plus, X, Copy, Check, Trash, EnvelopeSimple, Buildings, ShieldCheck, WarningCircle, Image as ImageIcon } from "@phosphor-icons/react";

const STATUS_BADGE = {
  open: "bg-cyan-500 text-white",
  used: "bg-slate-500 text-white",
  revoked: "bg-red-600 text-white",
  submitted: "bg-amber-500 text-white",
  reviewing: "bg-cyan-500 text-white",
  approved: "bg-emerald-500 text-white",
  rejected: "bg-red-600 text-white",
};

export default function LandlordOnboarding() {
  const [tab, setTab] = useState("submissions");
  const [tenant, setTenant] = useState(null);
  const [logoBust, setLogoBust] = useState(Date.now());

  useEffect(() => {
    api.get("/tenant").then(r => setTenant(r.data)).catch(() => {});
  }, []);

  const onLogoUploaded = () => {
    api.get("/tenant").then(r => setTenant(r.data)).catch(() => {});
    setLogoBust(Date.now());
    toast.success("Logo uploaded — will appear on emails & portals immediately");
  };

  return (
    <AdminShell>
      <div className="p-4 md:p-6 space-y-4" data-testid="onboarding-page">
        <div>
          <h1 className="text-4xl font-black uppercase tracking-widest flex items-center gap-2">
            <UserPlus weight="fill" className="text-emerald-500"/> Landlord Onboarding
          </h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            Invite new landlords and investors, capture their properties, docs, banking, service selections, and PA/Philly disclosures — one click auto-creates the landlord + property records.
          </p>
        </div>

        {tenant && <BrandingCheck tenant={tenant} logoBust={logoBust} onLogoUploaded={onLogoUploaded}
          onTenantUpdated={(t) => setTenant(t)}/>}

        <div className="flex flex-wrap gap-2 border-b-4 border-slate-900">
          {[
            { key: "submissions", label: "Submissions" },
            { key: "invites", label: "Invites" },
            { key: "radar", label: "Referral Radar" },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`onb-tab-${t.key}`}
              className={`h-10 px-4 font-black uppercase tracking-widest text-[11px] border-2 border-slate-900 -mb-1 ${
                tab === t.key ? "bg-slate-900 text-white shadow-[2px_2px_0_#0f172a]" : "bg-white text-slate-900 hover:bg-slate-100"
              }`}>{t.label}</button>
          ))}
        </div>

        {tab === "submissions" && <SubmissionsTab/>}
        {tab === "invites" && <InvitesTab/>}
        {tab === "radar" && <ReferralRadarTab/>}
      </div>
    </AdminShell>
  );
}

function BrandingCheck({ tenant, logoBust, onLogoUploaded, onTenantUpdated }) {
  const [name, setName] = useState(tenant.name || "");
  const [busy, setBusy] = useState(false);
  const [color, setColor] = useState(tenant.brand_color || "#0f172a");
  const fileRef = useRef(null);
  const API_BASE = process.env.REACT_APP_BACKEND_URL;
  const hasLogo = !!tenant.logo_path;

  const saveText = async () => {
    setBusy(true);
    try {
      await api.patch("/tenant", { name, brand_color: color });
      const r = await api.get("/tenant"); onTenantUpdated(r.data);
      toast.success("Branding saved");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed")); }
    finally { setBusy(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post("/tenant/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onLogoUploaded();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Upload failed")); }
  };

  return (
    <div className={`rugged-card p-3 border-l-8 ${hasLogo ? "border-emerald-500" : "border-amber-500"}`}
      data-testid="branding-check">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
          Your brand on emails &amp; landlord portals
        </div>
        {!hasLogo && (
          <span className="px-2 h-6 leading-6 bg-amber-500 text-white font-black uppercase tracking-widest text-[9px]">
            No logo — landlords see text fallback
          </span>
        )}
      </div>
      <div className="mt-3 grid grid-cols-1 md:grid-cols-[auto,1fr] gap-4 items-start">
        <div className="w-32 h-24 border-2 border-slate-300 bg-white flex items-center justify-center overflow-hidden shrink-0">
          {hasLogo ? (
            <img src={`${API_BASE}/api/tenant/logo/${tenant.id}?v=${logoBust}`} alt=""
              className="max-w-full max-h-full object-contain"
              data-testid="brand-logo-preview"
              onError={e => { e.currentTarget.style.display = "none"; }}/>
          ) : (
            <div className="text-[10px] text-slate-400 uppercase tracking-widest text-center px-2">No logo yet</div>
          )}
        </div>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-600 shrink-0 w-24">Company name</label>
            <input value={name} onChange={e => setName(e.target.value)}
              data-testid="brand-name"
              className="flex-1 h-9 px-2 border-2 border-slate-300 text-sm font-medium"/>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-600 shrink-0 w-24">Brand color</label>
            <input type="color" value={color} onChange={e => setColor(e.target.value)}
              data-testid="brand-color"
              className="h-9 w-16 border-2 border-slate-300"/>
            <input value={color} onChange={e => setColor(e.target.value)}
              className="h-9 px-2 border-2 border-slate-300 text-sm font-mono"/>
          </div>
          <div className="flex flex-wrap items-center gap-2 pt-2">
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
              data-testid="brand-logo-input"
              onChange={e => { const f = e.target.files?.[0]; if (f) uploadLogo(f); e.target.value = ""; }}/>
            <button onClick={() => fileRef.current?.click()} data-testid="brand-logo-btn"
              className="h-9 px-3 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-widest text-[10px] shadow-[2px_2px_0_#0f172a] inline-flex items-center gap-1">
              <ImageIcon size={12}/> {hasLogo ? "Replace Logo" : "Upload Logo"}
            </button>
            <button onClick={saveText} disabled={busy} data-testid="brand-save"
              className="h-9 px-3 border-2 border-slate-900 hover:bg-slate-100 font-black uppercase tracking-widest text-[10px] disabled:opacity-50">
              {busy ? "Saving…" : "Save Name / Color"}
            </button>
            <div className="text-[10px] text-slate-500">PNG or WEBP, transparent background, ≤ 3 MB</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReferralRadarTab() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  useEffect(() => {
    api.get("/landlord-onboarding/referral-radar")
      .then(r => setData(r.data))
      .catch(() => toast.error("Failed to load radar"))
      .finally(() => setBusy(false));
  }, []);
  const money = (c) => `$${((c || 0) / 100).toLocaleString()}`;

  if (busy) return <div className="text-sm text-slate-500 py-4">Loading…</div>;
  if (!data) return null;
  const { totals, channels } = data;
  if (totals.invites_sent === 0) return (
    <div className="rugged-card p-6 text-center text-sm text-slate-500">
      No referral data yet — start generating invites to see which channels convert best.
    </div>
  );
  const maxApproved = Math.max(1, ...channels.map(c => c.approved));

  return (
    <div className="space-y-4" data-testid="radar-tab">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {[
          ["Invites sent", totals.invites_sent, "bg-slate-900"],
          ["Submissions", totals.submissions, "bg-cyan-600"],
          ["Approved", totals.approved, "bg-emerald-600"],
          ["Properties acquired", totals.properties_acquired, "bg-fuchsia-600"],
          [`${totals.overall_conversion_pct}% conversion`, money(totals.monthly_rent_acquired_cents) + "/mo", "bg-amber-600"],
        ].map(([label, val, bg]) => (
          <div key={label} className={`p-3 ${bg} text-white shadow-[3px_3px_0_#0f172a]`}>
            <div className="text-[10px] font-black uppercase tracking-widest opacity-70">{label}</div>
            <div className="text-2xl font-black mt-1">{val}</div>
          </div>
        ))}
      </div>

      <div className="rugged-card p-4">
        <h3 className="font-black uppercase tracking-widest text-sm mb-3">Channel Leaderboard</h3>
        <div className="space-y-2">
          {channels.map((c, idx) => (
            <div key={c.channel} className="p-3 border-2 border-slate-200 space-y-2"
              data-testid={`radar-row-${idx}`}>
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <div className={`h-7 w-7 inline-flex items-center justify-center font-black text-[11px] ${
                      idx === 0 ? "bg-amber-500 text-white" :
                      idx === 1 ? "bg-slate-400 text-white" :
                      idx === 2 ? "bg-orange-500 text-white" : "bg-slate-200 text-slate-700"
                    }`}>#{idx + 1}</div>
                    <div className="font-black text-sm">{c.channel}</div>
                  </div>
                  <div className="text-[10px] text-slate-500 mt-1">
                    {c.invites_sent} invited · {c.submissions} submitted · <b className="text-emerald-700">{c.approved} approved</b>
                    {c.pending_review > 0 && <> · <b className="text-amber-700">{c.pending_review} pending</b></>}
                    {c.rejected > 0 && <> · {c.rejected} rejected</>}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xl font-black text-emerald-700">{c.approval_rate_pct}%</div>
                  <div className="text-[10px] text-slate-500">approval rate</div>
                </div>
              </div>
              {c.approved > 0 && (
                <div>
                  <div className="h-2 bg-slate-100">
                    <div className="h-full bg-emerald-500" style={{ width: `${(c.approved / maxApproved) * 100}%` }}/>
                  </div>
                  <div className="flex items-center justify-between text-[10px] text-slate-600 mt-1">
                    <span><b>{c.properties_acquired}</b> properties · <b>{money(c.monthly_rent_acquired_cents)}</b>/mo rent under management</span>
                    {c.top_services?.length > 0 && (
                      <span className="text-slate-500">Top service: <b>{c.top_services[0][0].replace(/_/g, " ")}</b></span>
                    )}
                  </div>
                </div>
              )}
              {c.recent_landlords.length > 0 && (
                <div className="text-[10px] text-slate-500 pt-1 border-t border-slate-100">
                  Recent: {c.recent_landlords.slice(0, 3).map(l => `${l.name} (${l.status})`).join(" · ")}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function InvitesTab() {
  const [invites, setInvites] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    setBusy(true);
    try { const r = await api.get("/landlord-onboarding/invites"); setInvites(r.data || []); }
    catch { toast.error("Failed to load invites"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const revoke = async (id) => {
    if (!window.confirm("Revoke this invite? The link stops working immediately.")) return;
    try { await api.delete(`/landlord-onboarding/invites/${id}`); toast.success("Revoked"); load(); }
    catch { toast.error("Revoke failed"); }
  };
  const copyLink = async (link) => {
    await navigator.clipboard.writeText(link);
    toast.success("Link copied");
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <div className="text-sm text-slate-600">{invites.length} invites total</div>
        <button onClick={() => setShowNew(true)} data-testid="new-invite-btn"
          className="h-11 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-widest text-[11px] shadow-[3px_3px_0_#0f172a] inline-flex items-center gap-1">
          <Plus size={14}/> Generate Invite
        </button>
      </div>

      {busy ? <div className="text-sm text-slate-500 py-4">Loading…</div>
      : invites.length === 0 ? (
        <div className="rugged-card p-6 text-center text-sm text-slate-500">
          No invites yet. Generate one to send to a new landlord.
        </div>
      ) : (
        <div className="space-y-2">
          {invites.map(inv => (
            <div key={inv.id} className="p-3 rugged-card space-y-1" data-testid={`invite-${inv.id}`}>
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-black text-sm">{inv.invited_name || "(no name)"}</div>
                    <span className={`px-2 h-6 leading-6 font-black uppercase tracking-widest text-[9px] ${STATUS_BADGE[inv.status]}`}>
                      {inv.status}
                    </span>
                  </div>
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    {inv.invited_email || "no email"} · {inv.invited_phone || "no phone"}
                    {inv.referral_source && ` · via ${inv.referral_source}`}
                  </div>
                  {inv.status === "open" && (
                    <div className="mt-1 flex items-center gap-1">
                      <code className="text-[10px] bg-slate-100 px-2 py-1 truncate max-w-md">{inv.link}</code>
                      <button onClick={() => copyLink(inv.link)} data-testid={`copy-${inv.id}`}
                        className="h-7 px-2 border-2 border-slate-900 hover:bg-slate-50 font-black uppercase tracking-widest text-[10px] inline-flex items-center gap-1">
                        <Copy size={11}/> Copy
                      </button>
                    </div>
                  )}
                  {inv.notes && <div className="text-[11px] text-slate-600 mt-1">{inv.notes}</div>}
                  <div className="text-[9px] text-slate-400 mt-1">
                    Created {inv.created_at?.slice(0, 10)} by {inv.created_by_name || "?"} · expires {inv.expires_at?.slice(0, 10)}
                  </div>
                </div>
                {inv.status === "open" && (
                  <div className="flex flex-col gap-1 shrink-0">
                    {inv.invited_email && (
                      <button onClick={async () => {
                        try {
                          const r = await api.post(`/landlord-onboarding/invites/${inv.id}/send-email`);
                          toast.success(r.data?.email_sent
                            ? `Emailed to ${r.data.to}`
                            : "Email killswitch blocked — link is copyable above");
                        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Send failed")); }
                      }} data-testid={`email-${inv.id}`}
                        className="h-8 px-2 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-widest text-[10px] inline-flex items-center justify-center gap-1">
                        <EnvelopeSimple size={12}/> Email
                      </button>
                    )}
                    <button onClick={() => revoke(inv.id)} data-testid={`revoke-${inv.id}`}
                      className="h-8 w-8 border-2 border-red-600 text-red-600 hover:bg-red-50 inline-flex items-center justify-center">
                      <Trash size={14}/>
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showNew && <NewInviteModal onClose={() => setShowNew(false)}
        onSaved={() => { setShowNew(false); load(); }}/>}
    </div>
  );
}

function NewInviteModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    invited_name: "", invited_email: "", invited_phone: "",
    referral_source: "", notes: "", expires_days: 30,
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.post("/landlord-onboarding/invites", {
        ...form, expires_days: parseInt(form.expires_days) || 30,
      });
      setResult(r.data);
      toast.success("Invite ready — copy the link");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
    finally { setBusy(false); }
  };
  const copy = async () => {
    await navigator.clipboard.writeText(result.link);
    toast.success("Copied");
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-[80] flex items-center justify-center p-4" data-testid="new-invite-modal">
      <div className="bg-white w-full max-w-lg border-4 border-slate-900 shadow-[6px_6px_0_#0f172a]">
        <div className="p-3 bg-emerald-600 text-white flex items-start justify-between">
          <div className="font-black uppercase tracking-widest">Generate Onboarding Invite</div>
          <button onClick={() => { setResult(null); onClose(); }} data-testid="invite-modal-close"
            className="p-1 hover:bg-emerald-700"><X size={18}/></button>
        </div>
        {result ? (
          <div className="p-4 space-y-3">
            <div className="p-3 bg-emerald-50 border-l-4 border-emerald-500 text-sm">
              <div className="font-black uppercase tracking-widest text-[10px] text-emerald-700 mb-1">Invite ready</div>
              Send this link to {result.invited_name || "the landlord"} — it works for {form.expires_days} days.
            </div>
            <input readOnly value={result.link} data-testid="new-invite-link"
              className="w-full h-10 px-3 border-2 border-slate-900 text-xs font-mono"/>
            <button onClick={copy} data-testid="copy-new-invite"
              className="w-full h-11 bg-slate-900 text-white font-black uppercase tracking-widest text-sm shadow-[3px_3px_0_#0f172a] inline-flex items-center justify-center gap-1">
              <Copy size={14}/> Copy Link
            </button>
            <button onClick={() => { setResult(null); onSaved(); }} data-testid="invite-done"
              className="w-full h-10 border-2 border-slate-900 hover:bg-slate-100 font-black uppercase tracking-widest text-[11px]">
              Done
            </button>
          </div>
        ) : (
          <div className="p-4 space-y-2">
            <input value={form.invited_name} onChange={e => setForm({...form, invited_name: e.target.value})}
              placeholder="Landlord name" data-testid="invite-name"
              className="w-full h-10 px-3 border-2 border-slate-300 text-sm"/>
            <div className="grid grid-cols-2 gap-2">
              <input value={form.invited_email} onChange={e => setForm({...form, invited_email: e.target.value})}
                placeholder="Email" data-testid="invite-email"
                className="h-10 px-3 border-2 border-slate-300 text-sm"/>
              <input value={form.invited_phone} onChange={e => setForm({...form, invited_phone: e.target.value})}
                placeholder="Phone"
                className="h-10 px-3 border-2 border-slate-300 text-sm"/>
            </div>
            <select value={form.referral_source} onChange={e => setForm({...form, referral_source: e.target.value})}
              data-testid="invite-referral"
              className="w-full h-10 px-3 border-2 border-slate-300 text-sm">
              <option value="">— Referral source (optional) —</option>
              <option>Existing landlord referral</option>
              <option>BiggerPockets</option>
              <option>Google</option>
              <option>Zillow / Rent listing</option>
              <option>Realtor referral</option>
              <option>Direct outreach</option>
              <option>Instagram / Social</option>
              <option>Word of mouth</option>
              <option>Other</option>
            </select>
            <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2}
              placeholder="Notes (property count, expected close date, etc.)"
              className="w-full p-2 border-2 border-slate-300 text-sm"/>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-600 flex flex-col gap-1">
              Expires in (days)
              <input type="number" value={form.expires_days} onChange={e => setForm({...form, expires_days: e.target.value})}
                className="h-10 px-3 border-2 border-slate-300 text-sm"/>
            </label>
            <button onClick={save} disabled={busy} data-testid="invite-save-btn"
              className="w-full h-11 bg-emerald-600 text-white font-black uppercase tracking-widest text-sm disabled:opacity-50 shadow-[3px_3px_0_#0f172a]">
              {busy ? "Generating…" : "Generate Invite Link"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function SubmissionsTab() {
  const [subs, setSubs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setBusy(true);
    try { const r = await api.get("/landlord-onboarding/submissions"); setSubs(r.data || []); }
    catch { toast.error("Failed to load submissions"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const money = (c) => c ? `$${(c/100).toLocaleString()}` : "—";

  return (
    <div className="space-y-3">
      {busy ? <div className="text-sm text-slate-500 py-4">Loading…</div>
      : subs.length === 0 ? (
        <div className="rugged-card p-6 text-center text-sm text-slate-500">
          No submissions yet. Landlords who complete the intake form will appear here.
        </div>
      ) : (
        <div className="space-y-2">
          {subs.map(s => (
            <button key={s.id} onClick={() => setDetail(s.id)} data-testid={`sub-${s.id}`}
              className="w-full text-left p-3 rugged-card hover:border-emerald-500 space-y-1">
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-black text-sm">
                      {s.contact?.company_llc || s.contact?.full_name || "(no name)"}
                    </div>
                    <span className={`px-2 h-6 leading-6 font-black uppercase tracking-widest text-[9px] ${STATUS_BADGE[s.status]}`}>
                      {s.status}
                    </span>
                  </div>
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    {s.contact?.email} · {s.contact?.phone}
                  </div>
                  <div className="text-[10px] text-slate-600 mt-1">
                    <Buildings weight="fill" size={11} className="inline mr-1"/>
                    {(s.properties || []).length} propert{(s.properties || []).length === 1 ? "y" : "ies"}
                    {" · "}
                    {(s.services || []).length} service{(s.services || []).length === 1 ? "" : "s"}
                    {(s.documents || []).length > 0 && ` · ${s.documents.length} doc${s.documents.length === 1 ? "" : "s"}`}
                  </div>
                  {s.status === "approved" && s.created_landlord_id && (
                    <div className="text-[10px] text-emerald-700 mt-1 font-bold">
                      ✓ Landlord created — {s.created_property_count} properties added
                    </div>
                  )}
                </div>
                <div className="text-[10px] text-slate-400">{s.created_at?.slice(0, 10)}</div>
              </div>
            </button>
          ))}
        </div>
      )}

      {detail && <SubmissionDetail id={detail} onClose={() => { setDetail(null); load(); }}/>}
    </div>
  );
}

function SubmissionDetail({ id, onClose }) {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const r = await api.get(`/landlord-onboarding/submissions/${id}`); setS(r.data); }
    catch { toast.error("Failed to load"); }
  };
  useEffect(() => { load(); }, [id]);

  const money = (c) => c ? `$${(c/100).toLocaleString()}` : "—";
  const approve = async () => {
    if (!window.confirm("Approve this submission? Auto-creates landlord + properties in Field Radar.")) return;
    setBusy(true);
    try {
      const r = await api.post(`/landlord-onboarding/submissions/${id}/approve`);
      toast.success(`Landlord created · ${r.data.property_count} properties added`);
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Approval failed")); }
    finally { setBusy(false); }
  };
  const reject = async () => {
    const reason = window.prompt("Rejection reason (visible to office only):");
    if (!reason) return;
    setBusy(true);
    try {
      await api.post(`/landlord-onboarding/submissions/${id}/reject`, { reason });
      toast.success("Rejected");
      onClose();
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  if (!s) return null;
  const acks = [
    ["Lead paint disclosure", s.lead_paint_disclosure_ack],
    ["Fair housing", s.fair_housing_ack],
    ["PA Landlord-Tenant Act", s.pa_landlord_tenant_act_ack],
    ["Philly Good Cause", s.good_cause_awareness_ack],
    ["Security deposit ≤ 2 months (year 1)", s.security_deposit_two_month_cap_ack],
    ["PA radon awareness", s.radon_awareness_ack],
  ];

  return (
    <div className="fixed inset-0 bg-slate-900/70 z-[80] flex items-start justify-center p-4 overflow-y-auto" data-testid="sub-detail">
      <div className="bg-white w-full max-w-4xl border-4 border-slate-900 shadow-[6px_6px_0_#0f172a] my-4">
        <div className="p-3 bg-emerald-600 text-white flex items-start justify-between sticky top-0 z-10">
          <div className="min-w-0">
            <div className="text-[10px] font-black uppercase tracking-widest opacity-80">Onboarding submission</div>
            <div className="font-black uppercase tracking-widest text-sm truncate">
              {s.contact?.company_llc || s.contact?.full_name}
            </div>
            <div className="text-[10px] opacity-90 mt-0.5">
              {s.contact?.email} · Status: {s.status} · Signed: {s.signed_by_name || "—"} on {s.signed_at?.slice(0,10)}
            </div>
          </div>
          <button onClick={onClose} data-testid="sub-detail-close" className="p-1 hover:bg-emerald-700"><X size={20}/></button>
        </div>
        <div className="p-4 space-y-4">
          {/* Contact */}
          <Section title="Contact">
            <Row k="Full name" v={s.contact?.full_name}/>
            <Row k="Company / LLC" v={s.contact?.company_llc}/>
            <Row k="Principal (portfolio grouping)" v={s.contact?.principal_name}/>
            <Row k="Email" v={s.contact?.email}/>
            <Row k="Phone" v={s.contact?.phone}/>
            <Row k="Mailing" v={s.contact?.mailing_address}/>
          </Section>

          {/* Properties */}
          <Section title={`Properties (${(s.properties || []).length})`}>
            {(s.properties || []).map((p, i) => (
              <div key={i} className="p-2 border-2 border-slate-200 space-y-0.5 text-xs mb-2" data-testid={`sub-prop-${i}`}>
                <div className="font-black text-sm">{p.address}{p.unit && ` · ${p.unit}`}</div>
                <div className="text-[10px] text-slate-600">
                  {p.beds}bd · {p.baths}ba · {p.sqft ? `${p.sqft} sqft` : "—"} · built {p.year_built || "?"}
                </div>
                <div className="text-[10px]">
                  Rent: <b>{money(p.current_rent_cents)}</b> · Tenant: {p.current_tenant_name || "vacant"}
                  {p.lease_end && ` · lease ends ${p.lease_end}`}
                </div>
                <div className="text-[10px]">
                  Rental license: <b>{p.philly_rental_license_status}</b>{p.philly_rental_license_number && ` #${p.philly_rental_license_number}`}
                  {" · "}Lead cert current: <b>{p.lead_cert_current ? "yes" : "no"}</b>
                </div>
                {p.notes && <div className="text-[10px] text-slate-500 mt-0.5">{p.notes}</div>}
              </div>
            ))}
          </Section>

          {/* Services */}
          <Section title={`Services requested (${(s.services || []).length})`}>
            <div className="flex flex-wrap gap-1">
              {(s.services || []).map(sv => (
                <span key={sv} className="px-2 py-1 bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest">
                  {sv.replace(/_/g, " ")}
                </span>
              ))}
              {(s.services || []).length === 0 && <span className="text-xs text-slate-500">— none selected —</span>}
            </div>
          </Section>

          {/* Banking */}
          <Section title="Banking (last 4 only stored)">
            <Row k="Bank" v={s.banking?.bank_name}/>
            <Row k="Account holder" v={s.banking?.account_holder}/>
            <Row k="Routing" v={s.banking?.routing_last4 ? `••••${s.banking.routing_last4}` : ""}/>
            <Row k="Account" v={s.banking?.account_last4 ? `••••${s.banking.account_last4}` : ""}/>
            <Row k="Type" v={s.banking?.account_type}/>
          </Section>

          {/* Access */}
          <Section title="Access & Keys">
            <Row k="Lockbox code" v={s.access?.lockbox_code}/>
            <Row k="Alarm code" v={s.access?.alarm_code}/>
            <Row k="Key location" v={s.access?.key_location}/>
            <Row k="Notes" v={s.access?.access_notes}/>
          </Section>

          {/* Preferences */}
          <Section title="Preferences">
            <Row k="Vendor preferences" v={s.preferences?.vendor_prefs}/>
            <Row k="Spending cap" v={money(s.preferences?.spending_cap_cents)}/>
            <Row k="Emergency contact" v={s.preferences?.emergency_contact_name}/>
            <Row k="Emergency phone" v={s.preferences?.emergency_contact_phone}/>
            <Row k="Referral" v={s.preferences?.referral_source}/>
            <Row k="Special instructions" v={s.preferences?.special_instructions}/>
          </Section>

          {/* Documents */}
          <Section title={`Documents uploaded (${(s.documents || []).length})`}>
            {(s.documents || []).map((d, i) => (
              <div key={i} className="text-xs py-1 flex items-center gap-2">
                <ShieldCheck size={12} className="text-emerald-600"/>
                <b>{(d.kind || "").replace("onboarding_", "").replace(/_/g, " ")}</b>
                <span className="text-[10px] text-slate-500">{Math.round((d.size || 0) / 1024)} KB</span>
              </div>
            ))}
            {(s.documents || []).length === 0 && <div className="text-xs text-slate-500">— no documents uploaded —</div>}
          </Section>

          {/* Acks */}
          <Section title="PA / Philly Acknowledgments">
            <div className="grid grid-cols-2 gap-1 text-xs">
              {acks.map(([label, val]) => (
                <div key={label} className="flex items-center gap-2">
                  {val ? <Check size={14} className="text-emerald-600"/> : <WarningCircle size={14} className="text-red-500"/>}
                  <span className={val ? "" : "text-slate-500"}>{label}</span>
                </div>
              ))}
            </div>
          </Section>

          {/* Actions */}
          {(s.status === "submitted" || s.status === "reviewing") && (
            <div className="flex gap-2 pt-4 border-t-4 border-slate-900">
              <button onClick={approve} disabled={busy} data-testid="sub-approve"
                className="flex-1 h-12 bg-emerald-600 text-white font-black uppercase tracking-widest shadow-[3px_3px_0_#0f172a] disabled:opacity-50">
                {busy ? "Working…" : "Approve → Create Landlord + Properties"}
              </button>
              <button onClick={reject} disabled={busy} data-testid="sub-reject"
                className="h-12 px-4 bg-red-600 text-white font-black uppercase tracking-widest shadow-[3px_3px_0_#0f172a] disabled:opacity-50">
                Reject
              </button>
            </div>
          )}
          {s.status === "rejected" && s.rejection_reason && (
            <div className="p-3 bg-red-50 border-l-4 border-red-500 text-sm">
              <div className="text-[10px] font-black uppercase tracking-widest text-red-700">Rejected</div>
              {s.rejection_reason}
            </div>
          )}
          {s.status === "approved" && (
            <div className="p-3 bg-emerald-50 border-l-4 border-emerald-500 text-sm">
              <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700">Approved</div>
              Landlord created (id {s.created_landlord_id?.slice(-6)}) · {s.created_property_count} properties added to portfolio · approved by {s.approved_by_name}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="rugged-card p-3">
      <h3 className="font-black uppercase tracking-widest text-[11px] text-slate-700 mb-2 border-b-2 border-slate-900 pb-1">{title}</h3>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function Row({ k, v }) {
  if (!v && v !== 0) return null;
  return (
    <div className="grid grid-cols-3 gap-2 text-xs">
      <div className="text-slate-500">{k}</div>
      <div className="col-span-2 font-medium">{v}</div>
    </div>
  );
}
